/**
 * Client-side image validation and resize using <canvas>.
 *
 * Replaces server-side sharp processing for video generation flows.
 * Validates against model constraints and resizes to exact video dimensions.
 */

export interface ProcessOptions {
  /** Maximum file size in bytes */
  maxBytes?: number;
  /** Maximum dimension in pixels (width or height) */
  maxPx?: number;
  /** Minimum dimension in pixels (width or height) */
  minPx?: number;
  /** Maximum aspect ratio (max(w/h, h/w)) */
  maxAspectRatio?: number;
  /** Target width for exact resize (cover crop) */
  targetWidth?: number;
  /** Target height for exact resize (cover crop) */
  targetHeight?: number;
}

/**
 * Load an image source (base64 data URI or Blob) into an HTMLImageElement.
 */
function loadImage(input: string | Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      if (typeof input !== "string") URL.revokeObjectURL(img.src);
      resolve(img);
    };
    img.onerror = () => {
      if (typeof input !== "string") URL.revokeObjectURL(img.src);
      reject(new Error("Failed to load image"));
    };
    img.src = typeof input === "string" ? input : URL.createObjectURL(input);
  });
}

/**
 * Validate image dimensions against constraints.
 * Throws descriptive errors on constraint violations.
 */
function validateConstraints(
  width: number,
  height: number,
  options: ProcessOptions,
): void {
  const { minPx, maxAspectRatio } = options;

  if (minPx && (width < minPx || height < minPx)) {
    throw new Error(
      `Image too small: ${width}x${height}. Minimum dimension is ${minPx}px.`,
    );
  }

  if (maxAspectRatio) {
    const ratio = Math.max(width / height, height / width);
    if (ratio > maxAspectRatio) {
      throw new Error(
        `Image aspect ratio too extreme (${ratio.toFixed(1)}:1). Maximum is ${maxAspectRatio}:1.`,
      );
    }
  }
}

/**
 * Draw image onto canvas, optionally resizing.
 * When targetWidth/targetHeight are provided, uses cover-crop logic
 * (centers and fills, cropping excess — same as sharp's fit:cover).
 */
function drawToCanvas(
  img: HTMLImageElement,
  targetWidth: number,
  targetHeight: number,
  useCoverCrop: boolean,
): HTMLCanvasElement {
  const canvas = document.createElement("canvas");
  canvas.width = targetWidth;
  canvas.height = targetHeight;
  const ctx = canvas.getContext("2d")!;

  if (useCoverCrop) {
    // Cover crop: scale image to fill target, crop excess from center
    const scale = Math.max(targetWidth / img.width, targetHeight / img.height);
    const scaledW = img.width * scale;
    const scaledH = img.height * scale;
    const offsetX = (targetWidth - scaledW) / 2;
    const offsetY = (targetHeight - scaledH) / 2;
    ctx.drawImage(img, offsetX, offsetY, scaledW, scaledH);
  } else {
    ctx.drawImage(img, 0, 0, targetWidth, targetHeight);
  }

  return canvas;
}

/**
 * Export canvas to Blob, trying PNG first, then JPEG at decreasing quality
 * if the result exceeds maxBytes.
 */
async function exportBlob(
  canvas: HTMLCanvasElement,
  maxBytes?: number,
): Promise<Blob> {
  // Try PNG first
  const pngBlob = await canvasToBlob(canvas, "image/png");
  if (!maxBytes || pngBlob.size <= maxBytes) return pngBlob;

  // Fall back to JPEG at decreasing quality
  for (const quality of [0.92, 0.8, 0.6, 0.4]) {
    const jpegBlob = await canvasToBlob(canvas, "image/jpeg", quality);
    if (jpegBlob.size <= maxBytes) return jpegBlob;
  }

  // Return lowest quality JPEG even if it exceeds maxBytes
  return canvasToBlob(canvas, "image/jpeg", 0.4);
}

function canvasToBlob(
  canvas: HTMLCanvasElement,
  type: string,
  quality?: number,
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error("Canvas toBlob failed"));
      },
      type,
      quality,
    );
  });
}

/**
 * Process an image for upload: validate constraints, resize if needed, export as Blob.
 *
 * @param input - base64 data URI or Blob
 * @param options - validation constraints and optional target dimensions
 * @returns Processed image as a Blob ready for R2 upload
 */
export async function processImageForUpload(
  input: string | Blob,
  options: ProcessOptions,
): Promise<Blob> {
  const img = await loadImage(input);
  const { width, height } = img;

  validateConstraints(width, height, options);

  const { maxPx, targetWidth, targetHeight } = options;

  // Determine output dimensions
  let outW = width;
  let outH = height;
  let useCoverCrop = false;

  if (targetWidth && targetHeight) {
    // Exact resize to video dimensions (cover crop)
    outW = targetWidth;
    outH = targetHeight;
    useCoverCrop = true;
  } else if (maxPx && (width > maxPx || height > maxPx)) {
    // Proportional downscale to fit within maxPx
    const scale = maxPx / Math.max(width, height);
    outW = Math.round(width * scale);
    outH = Math.round(height * scale);
  }

  // Skip canvas if no resize needed and input is already a Blob
  if (outW === width && outH === height && input instanceof Blob) {
    if (!options.maxBytes || input.size <= options.maxBytes) return input;
  }

  const canvas = drawToCanvas(img, outW, outH, useCoverCrop);
  return exportBlob(canvas, options.maxBytes);
}
